/**
 * Map frozen EI InvestigationState → operator lifecycle stages for UI rails.
 */
import type { EiInvestigationState, EiUiStage } from '../types/engineeringIntelligence';
import type { ReportLifecycleStage } from '../components/projects/operate/incidents/InvestigationLifecycleRail';

export const EI_UI_STAGE_LABELS: Record<EiUiStage, string> = {
  incident: 'Incident',
  investigation: 'Investigation',
  investigation_complete: 'Investigation complete',
  engineering_design: 'Engineering design',
  package_ready: 'Package ready',
  awaiting_approval: 'Awaiting approval',
  executing: 'Executing',
  validation: 'Validation',
  reflection: 'Reflection',
  knowledge_update: 'Knowledge update',
  resolved: 'Resolved',
  needs_human: 'Needs you',
};

const STAGE_ORDER: EiUiStage[] = [
  'incident',
  'investigation',
  'investigation_complete',
  'engineering_design',
  'package_ready',
  'awaiting_approval',
  'executing',
  'validation',
  'reflection',
  'knowledge_update',
  'resolved',
];

export function mapEiStateToUiStage(state?: EiInvestigationState | null): EiUiStage {
  const s = String(state || '').toUpperCase();
  if (s === 'NEEDS_HUMAN') return 'needs_human';
  if (s === 'NEW_INCIDENT') return 'incident';
  if (
    [
      'COLLECTING_EVIDENCE',
      'FORMING_HYPOTHESES',
      'GATHERING_MORE_EVIDENCE',
      'PROVISIONAL_DIAGNOSIS',
      'SELF_REVIEW',
      'DIAGNOSED',
    ].includes(s)
  ) {
    return 'investigation';
  }
  if (s === 'INVESTIGATION_COMPLETE') return 'investigation_complete';
  if (s === 'ENGINEERING_DESIGN' || s === 'PLANNING') return 'engineering_design';
  if (s === 'IMPLEMENTATION_PACKAGE_READY') return 'package_ready';
  if (s === 'AWAITING_APPROVAL') return 'awaiting_approval';
  // Testing of an approved package (and its accepted results awaiting Execute).
  if (s === 'AWAITING_TESTING' || s === 'TESTS_READY' || s === 'READY_TO_EXECUTE') return 'validation';
  if (s === 'EXECUTING') return 'executing';
  if (s === 'VALIDATING') return 'validation';
  if (s === 'AGENT_REFLECTION' || s === 'EVALUATE_SUCCESS') return 'reflection';
  if (s === 'LEARNING' || s === 'REFLECTION' || s === 'KNOWLEDGE_UPDATE') return 'knowledge_update';
  if (s === 'RESOLVED' || s === 'CLOSED') return 'resolved';
  return 'investigation';
}

/** Build ReportLifecycleStage[] compatible with InvestigationLifecycleRail / Journey. */
export function buildEiLifecycleStages(state?: EiInvestigationState | null): ReportLifecycleStage[] {
  const current = mapEiStateToUiStage(state);
  if (current === 'needs_human') {
    const base = buildEiLifecycleStages('VALIDATING');
    return [
      ...base.map((s) => ({ ...s, status: s.status === 'active' ? ('completed' as const) : s.status })),
      { id: 'needs_human', label: 'Needs you', status: 'active', detail: String(state) },
    ];
  }
  const idx = STAGE_ORDER.indexOf(current);
  return STAGE_ORDER.map((id, i) => ({
    id,
    label: EI_UI_STAGE_LABELS[id],
    status: i < idx ? 'completed' : i === idx ? 'active' : 'pending',
    detail: i === idx ? String(state || '') : undefined,
  }));
}

/** Engineering → Testing → Execution waiting states, named for what they wait on. */
const EI_TESTING_STATE_LABELS: Record<string, string> = {
  AWAITING_TESTING: 'Awaiting testing',
  TESTS_READY: 'Test results ready',
  READY_TO_EXECUTE: 'Ready to execute',
  PLATFORM_BLOCKED: 'Platform issue',
};

export function eiStateLabel(state?: string | null): string {
  if (!state) return 'No run';
  const testing = EI_TESTING_STATE_LABELS[String(state).toUpperCase()];
  if (testing) return testing;
  const ui = mapEiStateToUiStage(state);
  return EI_UI_STAGE_LABELS[ui] || state;
}

/** Operator-facing cognitive phase — never expose FSM identifiers as product language. */
export type OperatorPhase =
  | 'observing'
  | 'reasoning'
  | 'deciding'
  | 'designing'
  | 'packaging'
  | 'acting'
  | 'proving'
  | 'aligning'
  | 'closed'
  | 'needs_you';

export const OPERATOR_PHASE_LABELS: Record<OperatorPhase, string> = {
  observing: 'Observing',
  reasoning: 'Reasoning',
  deciding: 'Deciding',
  designing: 'Designing',
  packaging: 'Package ready',
  acting: 'Acting',
  proving: 'Proving',
  aligning: 'Aligning',
  closed: 'Closed',
  needs_you: 'Needs you',
};

export function mapEiStateToOperatorPhase(state?: EiInvestigationState | null): OperatorPhase {
  const s = String(state || '').toUpperCase();
  if (s === 'NEEDS_HUMAN') return 'needs_you';
  if (s === 'CLOSED') return 'closed';
  if (['RESOLVED', 'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE', 'CONVERGING'].includes(s)) return 'aligning';
  if (['VALIDATING', 'AGENT_REFLECTION', 'EVALUATE_SUCCESS', 'AWAITING_TESTING', 'TESTS_READY'].includes(s)) return 'proving';
  if (s === 'EXECUTING' || s === 'READY_TO_EXECUTE') return 'acting';
  if (s === 'IMPLEMENTATION_PACKAGE_READY' || s === 'AWAITING_APPROVAL') return 'packaging';
  if (s === 'ENGINEERING_DESIGN' || s === 'PLANNING') return 'designing';
  if (s === 'INVESTIGATION_COMPLETE' || s === 'DIAGNOSED') return 'deciding';
  if (['FORMING_HYPOTHESES', 'PROVISIONAL_DIAGNOSIS', 'SELF_REVIEW'].includes(s)) return 'reasoning';
  return 'observing';
}

/** True when the primary canvas should be engineering design / package (not observe+hypothesize). */
export function isEngineeringDesignStage(state?: EiInvestigationState | null): boolean {
  const ui = mapEiStateToUiStage(state);
  return (
    ui === 'engineering_design' ||
    ui === 'package_ready' ||
    ui === 'awaiting_approval' ||
    ui === 'investigation_complete'
  );
}

export function operatorPhaseLabel(state?: string | null): string {
  return OPERATOR_PHASE_LABELS[mapEiStateToOperatorPhase(state)];
}

/** Engineering phases are shown only after the underlying cause is confirmed. */
export function operatorPhaseLabelForCause(state?: string | null, underlyingStatus?: string | null): string {
  const label = operatorPhaseLabel(state);
  if (underlyingStatus === 'Confirmed') return label;
  if (label === 'Designing' || label === 'Deciding' || label === 'Acting' || label === 'Package ready' || label === 'Proving') {
    return 'Reasoning';
  }
  return label;
}

/**
 * Investigation / engineering workflow position.
 * Independent of RCA certainty: "Investigation complete" is not "root cause confirmed."
 */
export type InvestigationWorkflowState =
  | 'not_started'
  | 'investigating'
  | 'investigation_complete'
  | 'engineering_design'
  | 'awaiting_approval'
  | 'executing'
  | 'validating'
  | 'completed';

export const INVESTIGATION_WORKFLOW_LABELS: Record<InvestigationWorkflowState, string> = {
  not_started: 'Not started',
  investigating: 'Investigating',
  investigation_complete: 'Investigation complete',
  engineering_design: 'Engineering design',
  awaiting_approval: 'Awaiting approval',
  executing: 'Executing',
  validating: 'Validating',
  completed: 'Completed',
};

/** Screen order. Labels name the step, not a root-cause verdict. */
export const INVESTIGATION_WORKFLOW_RAIL: Array<{ id: string; label: string }> = [
  { id: 'incident', label: 'Incident' },
  { id: 'investigation', label: 'Investigation' },
  { id: 'rca', label: 'RCA' },
  { id: 'engineering_decision', label: 'Engineering Decision' },
  { id: 'engineering_design', label: 'Engineering Design' },
  { id: 'approval', label: 'Approval' },
  { id: 'execution', label: 'Execution' },
  { id: 'validation', label: 'Validation' },
];

const RAIL_INDEX: Record<InvestigationWorkflowState, number> = {
  not_started: 0,
  investigating: 1,
  investigation_complete: 2,
  engineering_design: 4,
  awaiting_approval: 5,
  executing: 6,
  validating: 7,
  completed: 7,
};

export function mapEiStateToWorkflow(state?: string | null): InvestigationWorkflowState {
  const s = String(state || '').toUpperCase();
  if (!s || s === 'NEW_INCIDENT') return 'not_started';
  if (['RESOLVED', 'CLOSED', 'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE', 'CONVERGING'].includes(s)) {
    return 'completed';
  }
  if (['VALIDATING', 'AGENT_REFLECTION', 'EVALUATE_SUCCESS', 'AWAITING_TESTING', 'TESTS_READY'].includes(s)) return 'validating';
  if (s === 'EXECUTING' || s === 'READY_TO_EXECUTE') return 'executing';
  if (s === 'AWAITING_APPROVAL') return 'awaiting_approval';
  if (['ENGINEERING_DESIGN', 'PLANNING', 'IMPLEMENTATION_PACKAGE_READY'].includes(s)) {
    return 'engineering_design';
  }
  if (s === 'INVESTIGATION_COMPLETE' || s === 'DIAGNOSED') return 'investigation_complete';
  return 'investigating';
}

export function investigationWorkflowLabel(state?: string | null): string {
  return INVESTIGATION_WORKFLOW_LABELS[mapEiStateToWorkflow(state)];
}

/**
 * The investigation FSM can seal a package and transition to AWAITING_APPROVAL
 * (orchestrator: "package requires approval") before the underlying cause is
 * confirmed. That stored state stays intact. Presentation follows the RCA
 * lifecycle: approval is shown only once the underlying cause is confirmed.
 */
export function presentedInvestigationState(state?: string | null, underlyingStatus?: string | null): string {
  const s = String(state || '').toUpperCase();
  if (!s) return '';
  const premature = [
    'AWAITING_APPROVAL',
    'IMPLEMENTATION_PACKAGE_READY',
    'ENGINEERING_DESIGN',
    'PLANNING',
    'AWAITING_TESTING',
    'TESTS_READY',
    'READY_TO_EXECUTE',
    'EXECUTING',
    'VALIDATING',
  ].includes(s);
  if (underlyingStatus && underlyingStatus !== 'Confirmed' && premature) return 'COLLECTING_EVIDENCE';
  return s;
}

export function incidentHeaderStateLabel(state?: string | null, underlyingStatus?: string | null): string {
  const presented = presentedInvestigationState(state, underlyingStatus);
  if (!presented) return '';
  if (presented === 'AWAITING_APPROVAL') return 'Awaiting approval';
  if (EI_TESTING_STATE_LABELS[presented]) return EI_TESTING_STATE_LABELS[presented];
  if (presented === 'COLLECTING_EVIDENCE' && underlyingStatus && underlyingStatus !== 'Confirmed') {
    return 'Investigating';
  }
  return investigationWorkflowLabel(presented);
}

/** Compact progress. No tool calls, scores, or RCA conclusions. */
export function buildInvestigationWorkflowStages(
  state?: string | null,
  underlyingStatus?: string | null,
): ReportLifecycleStage[] {
  let workflow = mapEiStateToWorkflow(state);
  const causeOpen = Boolean(underlyingStatus) && underlyingStatus !== 'Confirmed';
  if (causeOpen && workflow !== 'not_started' && workflow !== 'investigating') {
    workflow = 'investigation_complete';
  }
  const idx = RAIL_INDEX[workflow];
  const doneThrough = !causeOpen && workflow === 'completed' ? INVESTIGATION_WORKFLOW_RAIL.length : idx;
  return INVESTIGATION_WORKFLOW_RAIL.map((stage, i) => ({
    id: stage.id,
    label: stage.label,
    status: i < doneThrough ? 'completed' : i === idx ? 'active' : 'pending',
  }));
}

export function investigationWorkflowSummary(
  state: string | null | undefined,
  underlyingStatus?: string | null,
): string {
  const workflow = mapEiStateToWorkflow(state);
  const confirmed = underlyingStatus === 'Confirmed';
  if (workflow === 'not_started') return 'Investigation has not started.';
  if (workflow === 'investigating') return 'Investigation in progress.';
  if (confirmed) return 'Root cause confirmed.';
  if (workflow === 'investigation_complete') {
    return 'Investigation complete — underlying cause not determined.';
  }
  // An engineering-stage field must not declare the investigation finished
  // while the underlying cause is still open.
  if (
    workflow === 'engineering_design' ||
    workflow === 'awaiting_approval' ||
    workflow === 'executing' ||
    workflow === 'validating'
  ) {
    return 'Investigation in progress.';
  }
  if (workflow === 'completed') {
    return 'Investigation complete — underlying cause not determined.';
  }
  return 'Investigation in progress.';
}
